import React, { useState, useRef } from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import { enReportServiceType } from "./helper";

const ServiceTypeFilter = ({
  serviceTypeFilter,
  setServiceTypeFilter,
  theme,
}: {
  serviceTypeFilter: number | null;
  setServiceTypeFilter: (filter: number | null) => void;
  theme: Theme;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const getDisplayText = () => {
    if (!serviceTypeFilter) return "All services";
    return enReportServiceType[serviceTypeFilter] || "All services";
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      setDropdownAnchorEl(null);
    } else {
      setIsOpen(true);
      setDropdownAnchorEl(event.currentTarget);
    }
  };

  const handleOptionClick = (serviceType: number | null) => {
    setServiceTypeFilter(serviceType);
    setIsOpen(false);
    setDropdownAnchorEl(null);
  };

  const closePopup = () => {
    setIsOpen(false);
    setDropdownAnchorEl(null);
  };

  // Close popup when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownAnchorEl &&
        !dropdownAnchorEl.contains(event.target as Node)
      ) {
        const target = event.target as Element;
        const isInDropdown = target?.closest(
          '[data-service-type-dropdown="true"]'
        );

        if (!isInDropdown) {
          closePopup();
        }
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, dropdownAnchorEl]);

  return (
    <div ref={containerRef} style={{ position: "relative", margin: "0 8px" }}>
      <div onClick={handleClick} style={styles.serviceTypeFilter(theme)}>
        <span
          style={{
            color: isOpen
              ? theme.colors_ds.PRIMARY_ICON_MAIN
              : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          }}
        >
          {getDisplayText()}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease",
          }}
        >
          <path
            d="M7.99943 11.0006C7.73424 11.0005 7.47992 10.8951 7.29243 10.7076L3.29243 6.70757C3.11027 6.51897 3.00948 6.26636 3.01176 6.00417C3.01403 5.74197 3.1192 5.49116 3.30461 5.30575C3.49002 5.12034 3.74083 5.01517 4.00303 5.0129C4.26523 5.01062 4.51783 5.11141 4.70643 5.29357L7.99943 8.58657L11.2924 5.29357C11.3847 5.19806 11.495 5.12188 11.617 5.06947C11.739 5.01706 11.8703 4.98947 12.003 4.98832C12.1358 4.98717 12.2675 5.01247 12.3904 5.06275C12.5133 5.11303 12.6249 5.18728 12.7188 5.28117C12.8127 5.37507 12.887 5.48672 12.9373 5.60962C12.9875 5.73251 13.0128 5.86419 13.0117 5.99697C13.0105 6.12975 12.9829 6.26097 12.9305 6.38297C12.8781 6.50498 12.8019 6.61532 12.7064 6.70757L8.70643 10.7076C8.51894 10.8951 8.26463 11.0005 7.99943 11.0006Z"
            fill={
              isOpen
                ? theme.colors_ds.PRIMARY_ICON_MAIN
                : theme.colors_ds.NEUTRAL_TEXT_DEFAULT
            }
          />
        </svg>
      </div>

      {isOpen && (
        <div
          data-service-type-dropdown="true"
          style={{
            ...styles.serviceTypeDropdown(theme),
            position: "absolute",
            top: "100%",
            left: "0",
            right: "0",
            zIndex: 1300,
            marginTop: "4px",
          }}
        >
          <div
            onClick={() => handleOptionClick(null)}
            style={styles.serviceTypeOption(theme, !serviceTypeFilter)}
          >
            All services
          </div>
          {Object.entries(enReportServiceType).filter(([key]) => key !== "8").map(([key, value]) => (
            <div
              key={key}
              onClick={() => handleOptionClick(Number(key))}
              style={styles.serviceTypeOption(
                theme,
                serviceTypeFilter === Number(key)
              )}
            >
              {value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  serviceTypeFilter: (theme: any) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 16px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "var(--font-Medium)",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.colorTokens.N[40],
    },
  }),
  serviceTypeDropdown: (theme: any) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    borderRadius: "8px",
    boxShadow: "var(--box-shadow-general)",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    maxHeight: "400px",
    padding: "12px",
  }),
  serviceTypeOption: (theme: any, isSelected: boolean) => ({
    padding: "8px 12px",
    fontSize: "12px",
    fontFamily: "var(--font-Medium)",
    color: isSelected
      ? theme.colors_ds.PRIMARY_TEXT_MAIN
      : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    backgroundColor: isSelected ? theme.colorTokens.N[40] : "transparent",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.colorTokens.N[40],
    },
  }),
};

export default ServiceTypeFilter;
